/*
 * Dashboard.jsx — Pantalla principal Little Blue (rol-aware)
 *
 * Conserva toda la lógica de datos del diseño anterior.
 * Rediseña la presentación:
 *   · Hero con gradiente, saludo y badge de rol
 *   · Tarjeta de progreso de pago
 *   · Checklist de tareas según rol (customer_type 1 / 2 / 3)
 *   · Próximo vencimiento como CTA
 *   · Plan de pagos completo
 *   · Asesor (mobile solamente — desktop usa el sidebar de LayOut)
 *
 * ZOOM-SAFE:
 *   · Todas las tipografías en em (heredan la escala del phone-screen o del html)
 *   · min-height en botones en lugar de height fija
 *   · flex-wrap: wrap en todos los contenedores clave
 *   · word-break: break-word en textos monetarios y etiquetas
 */

import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { useNavigate, useOutletContext, NavLink } from "react-router-dom";
import { useDataContext } from "../contexts/AuthContext";
import { useDebtsContext } from "../contexts/FeeContext";
import {
  getItems,
  getPaymentInformation,
  getFeeInfo,
  hasCustomerItemsSelected,
  fetchFeesWithInterest,
  fetchCustomersSummary,
  getOrderItemsExtras,
  verifyTermsAccepted,
  getSizesProgress,
  getOrderItemStatus,
} from "../services/api";
import Modal1            from "./Modal1";
import Modal2            from "./Modal2";
import Modal3            from "./Modal3";
import ModalInfoPago     from "./ModalIfoPago";
import ModalConfirmarItems from "./ModalConfirmarItems";
import logoPng           from "../images/Frame.png";
import Swal              from "sweetalert2";
import s                 from "../styles/Dashboard.module.css";
import { formatLbOrderCode } from "../utils/orderDisplay";

/* ─────────────────────────────────────────────────────────────────────────── */

function Dashboard() {
  const navigate = useNavigate();
  const { contextData }         = useDataContext();
  const { userDebts }           = useDebtsContext();
  /* Datos del asesor vienen desde LayOut via Outlet context */
  const { agent, customerType: ctxType, orderType: ctxOrderType } =
    useOutletContext() ?? {};

  const customer     = contextData?.customer ?? {};
  const customerType = ctxType    ?? customer.customer_type ?? 1;
  const orderType    = ctxOrderType ?? customer.order_type  ?? 1;

  /* ── Estados de datos ── */
  const [items,              setItems]              = useState([]);
  const [hasAnySizeApproved, setHasAnySizeApproved] = useState(false);
  const [hasSelectableItems, setHasSelectableItems] = useState(false);
  const [sizesProgress,      setSizesProgress]      = useState({ hasItems: false, allLoaded: true });
  const [needsReconfirm,     setNeedsReconfirm]     = useState(false); // compra a re-confirmar tras modificar desde Talles
  const [tutorMode,          setTutorMode]          = useState(null);  // tutor: "buy" | "track" (lo elige en el 1er ingreso)
  const [orderStep,          setOrderStep]          = useState(null);  // 0..3 estado del pedido (caminito)
  const [prendaExtra,        setPrendaExtra]        = useState(false);
  const [paymentInformation, setPaymentInformation] = useState({});
  const [paid,               setPaid]               = useState(0);
  const [total,              setTotal]              = useState(0);
  const [paymentMethod,      setPaymentMethod]      = useState("");
  const [feesWithInterest,   setFeesWithInterest]   = useState([]);
  const [hasPaymentsPending, setHasPendingPayments] = useState(false);
  const [hasPaymentsRecords, setHasPendingRecords]  = useState(false);
  const [extras,             setExtras]             = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [termsAccepted,      setTermsAccepted]      = useState(
    customer?.terms_accepted_at ? true : null
  );
  const [groupSummary,       setGroupSummary]       = useState(null);

  /* ── Estados de modales ── */
  const [showModal1,         setShowModal1]         = useState(false);
  const [showModal2,         setShowModal2]         = useState(false);
  const [showModalConfirmar, setShowModalConfirmar] = useState(false);
  const [showModalInfoPago,  setShowModalInfoPago]  = useState(false);
  const [isModal3Open,       setIsModal3Open]       = useState(false);

  /* ── Estado de flujo de prendas/pago (persiste en localStorage) ── */
  const [canCloseModal, setCanCloseModal] = useState(
    localStorage.getItem("canCloseModal") === "true",
  );
  const [vengodelModal1, setVengoDelModal1] = useState(
    localStorage.getItem("vengoDelModal1") === "true",
  );
  const [vengodelModalPagos, setVengoDelModalPagos] = useState(
    localStorage.getItem("vengodelModalPagos") === "true",
  );
  const [paymentMethodSelected, setPaymentMethodSelected] = useState(
    localStorage.getItem("paymentMethodSelected") === "true",
  );

  /* ── Botón informar pago ── */
  const [isBotonHabilitado, setIsBotonHabilitado] = useState(true);
  useEffect(() => {
    // Solo se deshabilita si hay un pago PENDIENTE de aprobación (evitar doble informe).
    // Antes usaba hasPaymentsRecords → todo cliente con 2+ pagos (base migrada de V3)
    // quedaba sin poder informar la siguiente cuota.
    setIsBotonHabilitado(!hasPaymentsPending);
  }, [hasPaymentsPending]);

  /* ── Cuota detalle (tooltip) ── */
  const [activeFeeDetailId, setActiveFeeDetailId] = useState(null);
  const [feeDetailById, setFeeDetailById] = useState({});
  const feeDetailRefs = useRef({});

  /* ════════════════════════════════════════
     FETCHING DE DATOS
  ════════════════════════════════════════ */

  /* Items del pedido */
  useEffect(() => {
    if (!customer.order_id) return;
    getItems(customer.order_id)
      .then((data) => {
        const result = Array.isArray(data.result) ? data.result : [];
        setItems(result);
        setHasAnySizeApproved(data.has_any_size_approved === true);
        setHasSelectableItems(result.some((i) => i.size_approved !== true));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customer.order_id]);

  /* Progreso de talles (para mostrar/ocultar el pendiente en el inicio) */
  useEffect(() => {
    if (!customer.id || !customer.order_id) return;
    getSizesProgress(customer.id, customer.order_id)
      .then((data) => setSizesProgress(data || { hasItems: false, allLoaded: true }))
      .catch(console.error);
  }, [customer.id, customer.order_id]);

  /* Estado del pedido (caminito): 200=Preproducción, 300=Producción, 370=Control, 470=Entrega. */
  useEffect(() => {
    if (!customer.lead_id || orderType === 2) return;
    const ids = (Array.isArray(items) ? items : []).map((i) => i.id).filter(Boolean);
    if (ids.length === 0) return;
    getOrderItemStatus(customer.lead_id, ids)
      .then((data) => {
        const st = data?.[0]?.order_item_status;
        const step = st >= 470 ? 3 : st >= 370 ? 2 : st >= 300 ? 1 : 0;
        setOrderStep(step);
      })
      .catch(console.error);
  }, [customer.lead_id, items, orderType]);

  /* ¿La compra quedó "a re-confirmar"? (se marca al modificar la selección desde Talles) */
  useEffect(() => {
    if (!customer.id || !customer.order_id) return;
    try {
      setNeedsReconfirm(
        localStorage.getItem(`mcb_compra_reconfirm_${customer.id}_${customer.order_id}`) === "1"
      );
    } catch (e) { /* ignore */ }
  }, [customer.id, customer.order_id]);

  /* Tutor: en el 1er ingreso elige si va a comprar o solo seguir el pedido. */
  useEffect(() => {
    if (!customer.id) return;
    try {
      setTutorMode(localStorage.getItem(`mcb_tutor_mode_${customer.id}`) || null);
    } catch (e) { /* ignore */ }
  }, [customer.id]);

  const elegirTutorMode = (mode) => {
    try { localStorage.setItem(`mcb_tutor_mode_${customer.id}`, mode); } catch (e) { /* ignore */ }
    setTutorMode(mode);
    // Avisa al LayOut (nav) para que muestre/oculte "Pagos" al instante.
    try { window.dispatchEvent(new Event("mcb-tutor-mode")); } catch (e) { /* ignore */ }
    if (mode === "buy") setShowModal1(true); // arranca el flujo de compra
  };

  /* Información de pago + cuotas con intereses */
  useEffect(() => {
    if (!customer.id) return;
    getPaymentInformation(customer.id, customer.order_id)
      .then((data) => {
        setPaymentInformation(data);
        setPaymentMethod(data.payment_method);
        setPaid(data.paid);
        setTotal(data.total);
        setHasPendingRecords(data.hasPaymentsRecords);
        setHasPendingPayments(data.hasPaymentsPending);
      })
      .catch(console.error);

    if (userDebts) {
      fetchFeesWithInterest(userDebts)
        .then(setFeesWithInterest)
        .catch(console.error);
    }
  }, [customer.id, customer.order_id, userDebts]);

  /* Si el usuario ya eligió prendas */
  useEffect(() => {
    if (!customer.id) return;
    if (orderType === 2 || !hasSelectableItems) {
      setShowModal1(false);
      setShowModal2(false);
      return;
    }
    hasCustomerItemsSelected(customer.id, customer.order_id)
      .then(({ hasItemSelected }) => {
        setPrendaExtra(hasItemSelected);
        // Ya no abrimos el pop-up automáticamente: si falta elegir, mostramos un
        // aviso con botón en el inicio (más ágil que el modal forzado en cada ingreso).
        setShowModal1(false);
      })
      .catch(console.error);
  }, [customer.id, customer.order_id, canCloseModal, hasAnySizeApproved, hasSelectableItems, orderType]);

  /* Extras por item */
  useEffect(() => {
    if (!customer.id) return;
    getOrderItemsExtras(customer.id)
      .then((data) => { setExtras(data); setLoading(false); })
      .catch(console.error);
  }, [customer.id, userDebts]);

  /* Términos y condiciones del tutor */
  useEffect(() => {
    if (customerType !== 3) {
      setTermsAccepted(true);
      return;
    }

    const hasTermsInContext = !!customer?.terms_accepted_at;
    if (hasTermsInContext) {
      setTermsAccepted(true);
      return;
    }

    if (!customer?.lead_id) {
      setTermsAccepted(null);
      return;
    }

    let cancelled = false;
    verifyTermsAccepted(customer.lead_id)
      .then((data) => {
        if (cancelled) return;
        setTermsAccepted(data?.accepted === true);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setTermsAccepted(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    customerType,
    customer?.lead_id,
    customer?.terms_accepted_at,
    customer?.terms_accepted_by,
  ]);

  /* Estado del grupo (tutor) */
  useEffect(() => {
    if (customerType !== 3 || !customer?.order_id) {
      setGroupSummary(null);
      return;
    }

    let cancelled = false;
    fetchCustomersSummary(customer.order_id, 1)
      .then((data) => {
        if (cancelled) return;
        const totalPaid = Number(data?.sum_total_paid || 0);
        const totalDebt = Number(data?.sum_total_debt || 0);
        const totalPending = Number(data?.sum_total_pending || 0);
        const progressPct = totalDebt > 0 ? Math.min(100, (totalPaid / totalDebt) * 100) : 0;

        setGroupSummary({
          paid: totalPaid,
          total: totalDebt,
          pending: totalPending,
          progress: progressPct,
        });
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setGroupSummary(null);
      });

    return () => {
      cancelled = true;
    };
  }, [customerType, customer?.order_id]);

  /* ════════════════════════════════════════
     CÁLCULOS
  ════════════════════════════════════════ */

  const progress = useMemo(() => {
    if (!total || parseFloat(total) === 0) return 0;
    return Math.min(100, (parseFloat(paid) / parseFloat(total)) * 100);
  }, [paid, total]);
  const hasOwnProgressCard = useMemo(() => {
    const ownTotal = Number(total || 0);
    const ownPaid = Number(paid || 0);
    return ownTotal > 0 || ownPaid > 0 || hasPaymentsPending || !!paymentMethod;
  }, [total, paid, hasPaymentsPending, paymentMethod]);
  const extraBreakdown = useMemo(
    () => (Array.isArray(extras) ? extras.filter(Boolean) : []),
    [extras]
  );

  /* Cuotas con semáforo de colores */
  const currentDate = new Date();
  const cuotasConEstilo = feesWithInterest.map((c) => {
    const extraType = Number(c.is_extra || 0);
    const exp = new Date(c.expiration_date);
    const formattedDate = exp.toLocaleDateString("es-ES", { timeZone: "UTC" });
    let colorClass = s.cuotaFutura;
    let hasExpired = false;
    if (currentDate > exp) {
      colorClass  = s.cuotaVencida;
      hasExpired  = true;
    } else if (
      exp.getMonth() === currentDate.getMonth() &&
      exp.getFullYear() === currentDate.getFullYear()
    ) {
      colorClass = s.cuotaProxima;
    }
    // Monto que el cliente realmente debe por esta cuota = capital pendiente + mora.
    const montoConMora = parseFloat(c.pending || 0) + parseFloat(c.interest || 0);
    return { ...c, is_extra: extraType, colorClass, hasExpired, formattedDate, montoConMora };
  });

  /* Próximo vencimiento */
  const proximaCuota = useMemo(() => {
    const pendientes = cuotasConEstilo
      .filter((c) => Number(c.is_extra || 0) === 0 && parseFloat(c.pending) > 0)
      .sort((a, b) => a.number - b.number);
    return pendientes[0] ?? null;
  }, [cuotasConEstilo]);
  const calculateExtraTotal = (item = {}) =>
    ((Number(item.extraQuantity) || 0) + (Number(item.extraDesign) || 0)) * (Number(item.quantity) || 0);
  const selectedItemSummary = useMemo(() => {
    const selectedItems = Array.isArray(paymentInformation?.items)
      ? paymentInformation.items
      : [];

    const labels = selectedItems
      .map((item) => {
        const baseName = [item?.name, item?.alias].filter(Boolean).join(" ").trim();
        return baseName || null;
      })
      .filter(Boolean);

    const uniqueLabels = [...new Set(labels)];
    return uniqueLabels.slice(0, 3).join(", ");
  }, [paymentInformation]);

  /* Checklist de tareas (dinámico por rol) */
  const tasks = useMemo(() => {
    const list = [];
    const itemsDone = prendaExtra || !hasSelectableItems;
    // "Elegida" SOLO si el backend tiene la forma de pago (cps.payment_selection_id). El flag
    // de localStorage mentía: quedaba pegado de un intento incompleto y marcaba elegida sin estarlo.
    const payDone   = !!paymentMethod;
    // El tutor en modo "seguimiento" no compra → no mostramos las tareas de compra/pago.
    const compraAplica = customerType !== 3 || tutorMode === "buy";

    /* ── Compra/pago (no aplica al tutor que solo hace seguimiento) ── */
    if (compraAplica) {
      list.push({
        id: "items",
        iconClass: "bi bi-bag-heart-fill",
        label: "Elegiste tus prendas",
        desc: itemsDone
          ? selectedItemSummary || "Prendas seleccionadas"
          : "Todavía no elegiste tus prendas",
        status: itemsDone ? "done" : "urgent",
        onClick: !itemsDone ? () => setShowModal1(true) : null,
      });
      list.push({
        id: "pay-method",
        iconClass: "bi bi-credit-card-2-front-fill",
        label: "Forma de pago elegida",
        desc: payDone
          ? (paymentMethod ? paymentMethod.toUpperCase() : "Forma de pago elegida")
          : "Todavía no elegiste una forma de pago",
        status: payDone ? "done" : "urgent",
        onClick: !payDone ? () => setShowModal2(true) : null,
      });
    }
    if (sizesProgress.hasItems) {
      const tallesDone = sizesProgress.allLoaded;
      const faltan = sizesProgress.pendingItems || 0;
      list.push({
        id: "sizes",
        iconClass: "bi bi-rulers",
        label: "Completá tus talles",
        desc: tallesDone
          ? "Talles cargados"
          : `${faltan} ${faltan === 1 ? "prenda" : "prendas"} sin talle`,
        status: tallesDone ? "done" : "pending",
        onClick: () => navigate("/mcb/talles"),
      });
    }
    list.push({
      id: "design-view",
      iconClass: "bi bi-palette-fill",
      label: customerType >= 2 ? "Aprobar boceto" : "Ver diseño",
      desc: "Esperando boceto de Little Blue",
      status: "pending",
      onClick: () => navigate("/mcb/prendas"),
    });

    /* ── Delegado y tutor ── */
    if (customerType >= 2) {
      list.splice(list.length - 1, 0,
        {
          id: "design-guide",
          iconClass: "bi bi-journal-check",
          label: "Completar guía de diseño",
          desc: "Completá los detalles de diseño por prenda",
          status: "pending",
          onClick: () => navigate("/mcb/prendas"),
        },
        {
          id: "design-images",
          iconClass: "bi bi-image-fill",
          label: "Subir imágenes de referencia",
          desc: "Opcional · ayuda al diseñador",
          status: "pending",
          onClick: () => navigate("/mcb/prendas"),
        },
      );
    }

    /* ── Solo tutor ── */
    if (customerType === 3 && termsAccepted !== null) {
      list.unshift({
        id: "terms",
        iconClass: "bi bi-file-earmark-text-fill",
        label: termsAccepted ? "Términos aceptados" : "Aceptar Términos y Condiciones",
        desc: termsAccepted
          ? "Ya está resuelto para este pedido"
          : "Requerido para confirmar el pedido",
        status: termsAccepted ? "done" : "urgent",
        onClick: termsAccepted ? null : () => navigate("/terminos"),
      });
    }

    return list;
  }, [
    prendaExtra, hasSelectableItems, paymentMethodSelected, paymentMethod,
    sizesProgress, customerType, tutorMode, navigate, selectedItemSummary, termsAccepted,
  ]);

  /* ════════════════════════════════════════
     HANDLERS DE MODALES
  ════════════════════════════════════════ */

  const handleNextClick = () => {
    if (hasAnySizeApproved) {
      Swal.fire({
        icon: "info", iconColor: "#d54ab7", confirmButtonColor: "#0072ce",
        title: "Atención",
        text: "No podés seleccionar método de pago porque hay prendas con talles aprobados.",
      });
      setShowModal1(false); setShowModal2(false); return;
    }
    setShowModal1(false); setShowModal2(true);
    setCanCloseModal(true); setPaymentMethodSelected(true);
    localStorage.setItem("paymentMethodSelected", "true");
    localStorage.setItem("canCloseModal", "true");
  };

  const handleCerrarModal2 = () => {
    setShowModal1(false); setVengoDelModal1(false);
    localStorage.setItem("vengoDelModal1", "false");
  };

  const handleAtrasClick = () => {
    setShowModal1(true); setShowModal2(false);
    setVengoDelModalPagos(true); setCanCloseModal(false);
    localStorage.setItem("vengodelModalPagos", "true");
    localStorage.setItem("canCloseModal", "false");
  };

  const handleAcceptClick = () => {
    setShowModal2(false); setShowModalConfirmar(true);
  };

  // Refresca los datos de la cuenta (forma de pago, selección, talles) sin recargar
  // la página, para que el pendiente "¿Qué querés comprar?" desaparezca al instante
  // cuando ya se eligió prendas + cantidades + forma de pago.
  const refreshAccount = () => {
    if (!customer.id || !customer.order_id) return;
    getPaymentInformation(customer.id, customer.order_id)
      .then((data) => {
        setPaymentInformation(data);
        setPaymentMethod(data.payment_method);
        setPaid(data.paid);
        setTotal(data.total);
        setHasPendingRecords(data.hasPaymentsRecords);
        setHasPendingPayments(data.hasPaymentsPending);
      })
      .catch(console.error);
    hasCustomerItemsSelected(customer.id, customer.order_id)
      .then(({ hasItemSelected }) => setPrendaExtra(hasItemSelected))
      .catch(console.error);
    getSizesProgress(customer.id, customer.order_id)
      .then((d) => setSizesProgress(d || { hasItems: false, allLoaded: true }))
      .catch(console.error);
  };

  const handleAceptar  = () => {
    setShowModalConfirmar(false);
    // Re-confirmada: limpiamos la marca de "modificó desde Talles".
    try {
      localStorage.removeItem(`mcb_compra_reconfirm_${customer.id}_${customer.order_id}`);
    } catch (e) { /* ignore */ }
    setNeedsReconfirm(false);
    refreshAccount();
  };
  const handleCancelar = () => { setShowModalConfirmar(false); setShowModal2(true); };

  const handleFormularioEnviado = () => setIsBotonHabilitado(false);
  const handleEditItems = () => {
    setShowModal1(true);
    setShowModal2(false);
    setVengoDelModal1(false);
    localStorage.setItem("vengoDelModal1", "false");
  };

  const canHoverFeeDetail = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const openFeeDetail = async (id) => {
    setActiveFeeDetailId(id);
    if (feeDetailById[id]) return;
    try {
      const detail = await getFeeInfo(id);
      setFeeDetailById((prev) => ({ ...prev, [id]: detail }));
    } catch {
      /* silencioso */
    }
  };

  const closeFeeDetail = () => {
    setActiveFeeDetailId(null);
  };

  const toggleFeeDetail = (id) => {
    setActiveFeeDetailId((prev) => (prev === id ? null : id));
    if (!feeDetailById[id]) {
      openFeeDetail(id);
    }
  };

  useEffect(() => {
    if (!activeFeeDetailId) return undefined;

    const handlePointerDown = (event) => {
      const activeNode = feeDetailRefs.current[activeFeeDetailId];
      if (!activeNode || activeNode.contains(event.target)) return;
      setActiveFeeDetailId(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActiveFeeDetailId(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeFeeDetailId]);

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */

  if (loading) {
    return (
      <div className="contcargando">
        <img src={logoPng} alt="Little Blue" className="imgcargando" />
        <h2 className="text-muted">CARGANDO...</h2>
        <div className="loader" />
      </div>
    );
  }

  const cap = (str) =>
    typeof str === "string" && str.length ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const firstName  = cap(customer.first_name || "");
  const displayOrderCode = formatLbOrderCode(customer.access_code || customer.username);
  const roleLabel  =
    customerType === 3 ? "Tutor" :
    customerType === 2 ? "Delegado/a" : "Usuario";
  const heroGrad   =
    customerType === 3 ? "linear-gradient(135deg,#059669,#065F46)" :
    customerType === 2 ? "linear-gradient(135deg,#7C3AED,#4C1D95)" :
                        "linear-gradient(135deg,#0072CE,#004E9A)";
  const progressGrad =
    customerType === 3 ? "linear-gradient(90deg,#0072CE,#7C3AED)" :
    customerType === 2 ? "linear-gradient(90deg,#7C3AED,#4C1D95)" :
                        "linear-gradient(90deg,#D449B8,#a020c8)";
  const groupProgressGrad = "linear-gradient(90deg,#059669,#065F46)";
  const renderProgressCard = ({
    label,
    progressValue,
    paidValue,
    totalValue,
    gradient,
    pendingText = "",
    cardClassName = "",
  }) => (
    <div className={`${s.progressCard} ${cardClassName}`.trim()}>
      <div className={s.progressHeader}>
        <span className={s.progressLabel}>{label}</span>
        <span className={s.progressPct}>{Math.round(progressValue || 0)}%</span>
      </div>
      <div className={s.progressTrack}>
        <div
          className={s.progressFill}
          style={{ width: `${Math.max(0, Math.min(100, progressValue || 0))}%`, background: gradient }}
        />
      </div>
      <div className={s.progressAmounts}>
        <div className={s.progressAmount}>
          <span className={s.progressAmountLabel}>Abonado</span>
          <strong className={s.progressAmountValue} style={{ color: "#00A651" }}>
            ${Number(paidValue || 0).toLocaleString("es-AR")}
          </strong>
        </div>
        <div className={`${s.progressAmount} ${s.progressAmountRight}`}>
          <span className={s.progressAmountLabel}>Total</span>
          <strong className={s.progressAmountValue}>
            ${Number(totalValue || 0).toLocaleString("es-AR")}
          </strong>
        </div>
      </div>
      {pendingText ? <p className={s.pendingBadge}>{pendingText}</p> : null}
    </div>
  );

  /* ─── Agente ─── */
  const agentFirstName = agent?.firstName ?? "";
  const agentSurname   = agent?.surname   ?? "";
  const agentPhone     = agent?.phone     ?? "";

  /* ─── Explicación por rol (qué puede hacer cada usuario según sus permisos) ─── */
  const roleHelp = {
    3: {
      title: "Sos el Tutor del curso",
      intro: "Centralizás la comunicación con Little Blue para todo el curso. Lo que más queremos es que tu rol sea simple y transparente, para eso contás con nuestra ayuda en todo momento. Desde acá podés:",
      can: [
        "Ver el avance de pagos de TODOS los alumnos del curso (solapa Alumnos)",
        "Elegir tus prendas, forma de pago, completar tus talles y cargar pagos para todo el curso",
        "Hacer el seguimiento de la cuenta de todos los alumnos",
        "Aceptar los Términos y Condiciones del pedido",
      ],
    },
    2: {
      title: "Sos Delegado/a del curso",
      intro: "Ayudás a centralizar la comunicación del curso. Desde acá podés:",
      can: [
        "Ver el resumen de cuenta de los alumnos",
        "Elegir tus prendas, forma de pago y completar tus talles",
        "Cargar y seguir tus pagos",
      ],
    },
    1: {
      title: "Tu cuenta",
      intro: "Acá gestionás tu pedido de principio a fin. Lo que tenés que hacer:",
      can: [
        "Elegir tus prendas y la forma de pago",
        "Completar tus talles",
        "Cargar tus pagos y seguir tu avance",
      ],
    },
  };
  const help = roleHelp[customerType] ?? roleHelp[1];

  /* ─── Accesos a secciones (según el rol, igual que el menú) ─── */
  // El tutor en modo "seguimiento" no tiene pagos personales → se le oculta Pagos.
  const showPagos = customerType !== 3 || tutorMode === "buy";
  const sectionLinks = [
    ...(showPagos
      ? [{ to: "/mcb/pagos", icon: "bi-wallet2", label: "Pagos", desc: "Cargá y seguí tus pagos" }]
      : []),
    ...(customerType === 3
      ? [{ to: "/mcb/accountsSummary", icon: "bi-people-fill", label: "Alumnos", desc: "Avance de todo el curso" }]
      : []),
    { to: "/mcb/prendas", icon: "bi-palette-fill", label: "Diseño", desc: "Tu diseño y boceto" },
    ...(orderType !== 2
      ? [{ to: "/mcb/entregas", icon: "bi-truck", label: "Entregas", desc: "Seguí tu envío" }]
      : []),
    ...(orderType !== 2
      ? [{ to: "/mcb/rifas", icon: "bi-ticket-perforated-fill", label: "Rifas", desc: "Tus rifas" }]
      : []),
    { to: "/mcb/faq", icon: "bi-question-circle-fill", label: "Preguntas frecuentes", desc: "Dudas comunes" },
  ];

  return (
    <>
      {/* ════ MODALES (se superponen al contenido) ════ */}

      {/* Tutor — 1er ingreso (después de T&C): ¿comprar o solo seguir el pedido? */}
      {customerType === 3 && termsAccepted === true && !tutorMode && orderType !== 2 && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(12,18,34,.68)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "#fff", borderRadius: "1.2em", padding: "1.6em 1.4em", maxWidth: 420, width: "100%", boxShadow: "0 24px 60px rgba(12,18,34,.3)", textAlign: "center" }}>
            <i className="bi bi-bag-heart-fill" style={{ fontSize: "2.2em", color: "#0071ce" }} />
            <h2 style={{ margin: ".3em 0 .2em", fontSize: "1.25em", color: "#0f172a", fontWeight: 800 }}>
              ¿Vas a comprar alguna prenda?
            </h2>
            <p style={{ margin: "0 0 1.1em", color: "#64748b", fontSize: ".95em" }}>
              Podés comprar prendas para vos o solo seguir el pedido del curso.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: ".6em" }}>
              <button type="button" onClick={() => elegirTutorMode("buy")}
                style={{ border: "none", borderRadius: ".8em", padding: ".85em", fontWeight: 800, fontSize: ".95em", cursor: "pointer", background: "#0071ce", color: "#fff" }}>
                <i className="bi bi-bag-plus" /> Sí, quiero comprar prendas
              </button>
              <button type="button" onClick={() => elegirTutorMode("track")}
                style={{ border: "1.5px solid #cbd5e1", borderRadius: ".8em", padding: ".85em", fontWeight: 700, fontSize: ".95em", cursor: "pointer", background: "#fff", color: "#334155" }}>
                <i className="bi bi-eye" /> Solo seguir el pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal1 && orderType !== 2 && (
        <Modal1
          onNextClick={handleNextClick}
          closeModal={handleCerrarModal2}
          setPaymentMethod={setPaymentMethod}
          paymentMethod={paymentMethod}
          vengodelModal1={setVengoDelModal1}
          vengodelModalPagos={vengodelModalPagos}
        />
      )}
      {showModal2 && orderType !== 2 && (
        <Modal2
          onAcceptClick={handleAcceptClick}
          onBackClick={() => setShowModal2(false)}
          setPaymentInformation={setPaymentInformation}
          setPaymentMethod={setPaymentMethod}
          setTotal={setTotal}
          setPaid={setPaid}
          vengodelModal1={vengodelModal1}
          vengodelModal2={setVengoDelModal1}
          handleAtrasClick={handleAtrasClick}
          isBlockedByApprovedSizes={hasAnySizeApproved}
        />
      )}
      {showModalConfirmar && (
        <ModalConfirmarItems
          handleAceptar={handleAceptar}
          handleCancelar={handleCancelar}
          setPaymentMethod={setPaymentMethod}
          userDebtsWithStyles={cuotasConEstilo}
        />
      )}
      {isModal3Open && (
        <Modal3
          onFormularioEnviado={handleFormularioEnviado}
          toggleModal3={() => setIsModal3Open(false)}
        />
      )}
      {showModalInfoPago && (
        <ModalInfoPago toggleModalInfo={() => setShowModalInfoPago(false)} />
      )}

      {/* ════ CONTENIDO PRINCIPAL ════ */}
      <div className={s.page}>

        {/* ── HERO: dos columnas (datos del pedido | pendientes) ── */}
        <div className={s.hero} style={{ background: heroGrad }}>
          <div className={s.heroSplit}>
            <div className={s.heroLeft}>
              <div className={s.heroOrder}>
                <span className={s.heroOrderLabel}>Pedido</span>
                <NavLink to="/switch" className={s.heroOrderCode}>
                  {displayOrderCode}
                </NavLink>
              </div>
              <h1 className={s.heroGreeting}>
                <span className={s.heroGreetingLight}>Hola, </span>
                {firstName}!
              </h1>
              <span className={s.heroBadge}>{roleLabel}</span>
            </div>

            <div className={s.heroRight}>
              {(() => {
                const itemsDone = prendaExtra || !hasSelectableItems;
                // "Elegida" SOLO si el backend tiene la forma de pago (cps.payment_selection_id). El flag
    // de localStorage mentía: quedaba pegado de un intento incompleto y marcaba elegida sin estarlo.
    const payDone   = !!paymentMethod;
                // Saldo pendiente relevante (> $100) → consideramos que hay deuda;
                // un resto chico cuenta como saldado y no muestra el ítem de Pagos.
                const hasDebt   = Number(total) - Number(paid) > 100;
                // Demorado = cuota vencida con saldo relevante (> $100; ignoramos
                // saldos chicos por redondeo/intereses mínimos).
                const demorado  = cuotasConEstilo.some(
                  (c) => c.hasExpired && parseFloat(c.pending) > 100
                );

                const pend = [];
                // El tutor solo tiene compra pendiente si en el 1er ingreso eligió "comprar".
                const compraAplica = customerType !== 3 || tutorMode === "buy";
                // 1) Compra: aparece hasta que eligió prendas Y forma de pago. También
                //    reaparece si modificó la selección desde Talles (needsReconfirm),
                //    para que re-confirme el pedido con el nuevo monto.
                if (orderType !== 2 && compraAplica && (!(itemsDone && payDone) || needsReconfirm)) {
                  pend.push({
                    key: "compra",
                    icon: "bi-bag-heart-fill",
                    label: "¿Qué querés comprar?",
                    onClick: () => (itemsDone ? setShowModal2(true) : setShowModal1(true)),
                  });
                }
                // 2) Pagos: solo si NO está saldado. Verde = al día, rojo = demorado.
                if (orderType !== 2 && hasDebt) {
                  pend.push({
                    key: "pagos",
                    icon: "bi-wallet2",
                    label: "Pagos pendientes",
                    dot: demorado ? "red" : "green",
                    onClick: () => navigate("/mcb/pagos"),
                  });
                }
                // 3) Talles: aparece hasta que TODAS las unidades tengan talle cargado.
                if (sizesProgress.hasItems && !sizesProgress.allLoaded) {
                  pend.push({
                    key: "talles",
                    icon: "bi-rulers",
                    label: "Cargá tus talles",
                    onClick: () => navigate("/mcb/talles"),
                  });
                }

                if (pend.length === 0) {
                  return (
                    <div className={s.pendPanel}>
                      <div className={s.pendAlDia}>
                        <i className="bi bi-check-circle-fill" />
                        <span>¡Estás al día!</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className={s.pendPanel}>
                    <span className={s.pendTitle}>Pendientes</span>
                    {pend.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className={s.pendRow}
                        onClick={p.onClick}
                      >
                        <i className={`bi ${p.icon} ${s.pendIcon}`} />
                        <span className={s.pendRowLabel}>{p.label}</span>
                        {p.dot ? (
                          <span
                            className={`${s.pendDot} ${
                              p.dot === "red" ? s.pendDotRed : s.pendDotGreen
                            }`}
                          />
                        ) : null}
                        <i className={`bi bi-chevron-right ${s.pendChevron}`} />
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ── ESTADO DEL PEDIDO (caminito) ── */}
        {orderStep !== null && orderType !== 2 && (
          <section className={s.section}>
            <div style={{ background: "#fff", borderRadius: "1em", padding: "1em 1.1em", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
              <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: ".9em", fontSize: "1.02em" }}>
                <i className="bi bi-truck" style={{ color: "#0071ce", marginRight: ".4em" }} />
                Estado de tu pedido
              </div>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                {["Preproducción", "Producción", "Control de Calidad", "Entrega"].map((label, i) => {
                  const done = i < orderStep;
                  const current = i === orderStep;
                  const active = i <= orderStep;
                  return (
                    <Fragment key={label}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "0 0 auto", width: "4em" }}>
                        <div style={{
                          width: "1.9em", height: "1.9em", borderRadius: "50%", display: "flex",
                          alignItems: "center", justifyContent: "center", fontSize: ".8em", fontWeight: 800,
                          background: active ? "#0071ce" : "#e2e8f0", color: active ? "#fff" : "#94a3b8",
                          boxShadow: current ? "0 0 0 4px rgba(0,113,206,.18)" : "none",
                        }}>
                          {done ? <i className="bi bi-check-lg" /> : i + 1}
                        </div>
                        <span style={{ marginTop: ".45em", fontSize: ".68em", textAlign: "center", lineHeight: 1.15, color: current ? "#0071ce" : "#64748b", fontWeight: current ? 700 : 500 }}>
                          {label}
                        </span>
                      </div>
                      {i < 3 && (
                        <div style={{ flex: 1, height: 3, borderRadius: 2, marginTop: "calc(.95em - 1.5px)", background: i < orderStep ? "#0071ce" : "#e2e8f0" }} />
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ── EXPLICACIÓN / CÓMO FUNCIONA (según el rol y sus permisos) ── */}
        <section className={s.section}>
          <div style={{ background: "#fff", borderRadius: "1em", padding: "1em 1.1em", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
            <h2 style={{ margin: "0 0 .15em", fontSize: "1.1em", color: "#0f172a" }}>{help.title}</h2>
            <p style={{ margin: "0 0 .5em", color: "#475569", fontSize: ".95em" }}>{help.intro}</p>
            <ul style={{ margin: 0, paddingLeft: "1.2em", color: "#334155", fontSize: ".95em" }}>
              {help.can.map((c, i) => (
                <li key={i} style={{ marginBottom: ".2em" }}>{c}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── ACCESO A CADA SECCIÓN ── */}
        <section className={s.section}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(8.5em, 1fr))", gap: ".6em" }}>
            {sectionLinks.map((sec) => (
              <NavLink
                key={sec.to}
                to={sec.to}
                style={{ textDecoration: "none", color: "inherit", background: "#fff", borderRadius: ".8em", padding: ".8em", boxShadow: "0 1px 3px rgba(0,0,0,.08)", display: "flex", flexDirection: "column", gap: ".2em" }}
              >
                <i className={`bi ${sec.icon}`} style={{ fontSize: "1.4em", color: "#0d6efd" }} aria-hidden="true" />
                <strong style={{ fontSize: ".95em" }}>{sec.label}</strong>
                <span style={{ fontSize: ".8em", color: "#64748b" }}>{sec.desc}</span>
              </NavLink>
            ))}
          </div>
        </section>

        {/* ── TARJETAS DE PROGRESO ── */}
        <div className={s.progressCardsGrid}>
          {customerType === 3 && groupSummary
            ? renderProgressCard({
                label: "Estado del grupo",
                progressValue: groupSummary.progress,
                paidValue: groupSummary.paid,
                totalValue: groupSummary.total,
                gradient: groupProgressGrad,
                pendingText: groupSummary.pending > 0 ? "El grupo todavía tiene saldo pendiente" : "",
                cardClassName: s.progressCardPrimary,
              })
            : null}

          {(customerType !== 3 || hasOwnProgressCard)
            ? renderProgressCard({
                label: "Tu avance de pago",
                progressValue: progress,
                paidValue: paid,
                totalValue: total,
                gradient: progressGrad,
                pendingText: hasPaymentsPending ? "Tiene pagos pendientes de aprobación" : "",
                cardClassName: customerType === 3 ? s.progressCardSecondary : s.progressCardPrimary,
              })
            : null}
        </div>

        {/* ── BANNER TÉRMINOS (tutor solamente) ── */}
        {customerType === 3 && termsAccepted === false && (
          <div className={s.termsBanner}>
            <p className={s.termsBannerTitle}>⚠️ Acción requerida</p>
            <p className={s.termsBannerDesc}>
              Necesitás aceptar los Términos y Condiciones para que el pedido quede confirmado.
            </p>
            <button className={s.termsBtn} onClick={() => navigate("/terminos")}>
              Leer y aceptar Términos y Condiciones
            </button>
          </div>
        )}

        {/* ── RESUMEN DE CUENTA (tutor) ── */}
        {(customerType === 2 || customerType === 3) && (
          <section className={s.section}>
            <button
              className={s.summaryBtn}
              onClick={() => navigate("/mcb/accountsSummary")}
            >
              <i className="bi bi-people-fill" aria-hidden="true" /> Ver resumen de cuenta de todos los alumnos
            </button>
          </section>
        )}

        {/* ── PRÓXIMO VENCIMIENTO ── */}
        {proximaCuota && orderType !== 2 && (
          <section className={s.section}>
            <h2 className={s.sectionTitle}>Próximo vencimiento</h2>
            <div className={s.nextPayCard}>
              <span className={s.nextPayIcon}><i className="bi bi-calendar2-week-fill" aria-hidden="true" /></span>
              <div className={s.nextPayBody}>
                <span className={s.nextPayLabel}>
                  {proximaCuota.number}° cuota
                </span>
                <span className={s.nextPayAmount}>
                  ${parseFloat(proximaCuota.montoConMora).toLocaleString("es-AR")}
                </span>
                <span className={s.nextPayDate}>
                  Vence {proximaCuota.formattedDate}
                </span>
              </div>
              <button
                className={s.nextPayBtn}
                onClick={() => setIsModal3Open(true)}
                disabled={!isBotonHabilitado || orderType === 2}
              >
                Informar pago
              </button>
            </div>
          </section>
        )}

        {/* ── MÉTODO DE PAGO + ACCIONES SECUNDARIAS ── */}
        {orderType !== 2 && paymentMethod && (
          <section className={s.section}>
            <div className={s.payMethodRow}>
              <span className={s.payMethodLabel}>
                Modalidad de pago:{" "}
                <strong className={s.payMethodValue}>
                  {paymentMethod.toUpperCase()}
                </strong>
              </span>
              <div className={s.payMethodActions}>
                <button
                  className={s.btnSecondary}
                  onClick={() => setShowModalInfoPago(true)}
                >
                  Más info
                </button>
                <button
                  className={`${s.btnSecondary} ${(!isBotonHabilitado || hasAnySizeApproved) ? s.btnDisabled : ""}`}
                  disabled={!isBotonHabilitado || hasAnySizeApproved}
                  onClick={() => {
                    setShowModal2(true);
                    setVengoDelModal1(false);
                  }}
                >
                  Cambiar
                </button>
                <button
                  className={`${s.btnSecondary} ${(!isBotonHabilitado || orderType === 2) ? s.btnDisabled : ""}`}
                  disabled={!isBotonHabilitado || orderType === 2}
                  onClick={handleEditItems}
                >
                  Editar prendas
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── PLAN DE PAGOS COMPLETO ── */}
        {cuotasConEstilo.length > 0 && (
          <section className={s.section}>
            <h2 className={s.sectionTitle}>Plan de pagos</h2>

            <div className={s.feeList}>
              {/* Cuotas normales */}
              {cuotasConEstilo
                .filter((c) => Number(c.is_extra || 0) === 0)
                .sort((a, b) => a.number - b.number)
                .map((c) => (
                  <div key={c.id} className={`${s.feeRow} ${c.colorClass}`}>
                    <span className={s.feeNum}>{c.number}°</span>
                    <div className={s.feeBody}>
                      <span className={s.feeName}>{c.number}° Cuota</span>
                      <span className={s.feeDate}>{c.formattedDate}</span>
                    </div>
                    <span className={s.feeAmount}>
                      ${parseFloat(c.montoConMora).toLocaleString("es-AR")}
                    </span>
                    <span className={`${s.feeDot} ${s[`feeDot_${c.colorClass.replace(s.cuota, "")}`]}`} />
                    {c.hasExpired && (
                      <div
                        className={s.feeQuestionWrap}
                        ref={(node) => {
                          if (node) {
                            feeDetailRefs.current[c.id] = node;
                          } else {
                            delete feeDetailRefs.current[c.id];
                          }
                        }}
                        onMouseEnter={() => {
                          if (canHoverFeeDetail()) {
                            openFeeDetail(c.id);
                          }
                        }}
                        onMouseLeave={() => {
                          if (canHoverFeeDetail()) {
                            closeFeeDetail();
                          }
                        }}
                      >
                        <button
                          type="button"
                          className={s.feeQuestion}
                          onClick={() => toggleFeeDetail(c.id)}
                          aria-label="Ver detalle de mora"
                          aria-expanded={activeFeeDetailId === c.id ? "true" : "false"}
                        >
                          <i className="bi bi-question-circle-fill" aria-hidden="true" />
                        </button>
                        {activeFeeDetailId === c.id && feeDetailById[c.id] ? (
                          <div className={s.feeDetail} role="tooltip">
                            <div className={s.feeDetailHeader}>
                              <span className={s.feeDetailEyebrow}>Detalle de mora</span>
                            </div>
                            <p>
                              Cuota original: <strong>${feeDetailById[c.id].original_price}</strong>
                            </p>
                            <p>
                              Atraso por mora: <strong>${feeDetailById[c.id].interest}</strong>
                            </p>
                            <p>
                              Total: <strong>${feeDetailById[c.id].original_price + feeDetailById[c.id].interest}</strong>
                            </p>
                            <p className={s.feeDetailNote}>
                              Monto redondeado a valores sin decimales
                            </p>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}

              {/* Cuotas extra y prendas extra */}
              {cuotasConEstilo
                .filter((c) => Number(c.is_extra || 0) > 0)
                .map((c) => {
                  const isExtraCloth = Number(c.is_extra) === 2;
                  return (
                  <div
                    key={c.id}
                    className={`${s.feeRow} ${isExtraCloth ? s.feeRowPrendaExtra : s.feeRowExtra}`}
                  >
                    <span className={s.feeNum}>+</span>
                    <div className={s.feeBody}>
                      <span className={s.feeName}>
                        {isExtraCloth ? "Prenda extra" : "Cuota extra"}
                      </span>
                      <span className={s.feeDate}>{c.formattedDate}</span>
                    </div>
                    <span className={s.feeAmount}>
                      ${parseFloat(c.montoConMora).toLocaleString("es-AR")}
                    </span>
                    <span className={s.feeDot} />
                  </div>
                )})}
            </div>

            {/* Detalle de items (colapsado por defecto) */}
            {paymentInformation?.items && (
              <details className={s.itemsDetail}>
                <summary className={s.itemsDetailSummary}>
                  Ver detalle de prendas y financiamiento
                </summary>
                <ul className={s.itemsList}>
                  {paymentInformation.items.map((item, i) => (
                    <li key={i} className={s.itemsListItem}>
                      <span
                        className={s.itemName}
                        style={{ color: item.isExtraOrder ? "#D449B8" : "#0072CE" }}
                      >
                        {item.name} {item.alias}
                        <span className={s.itemQty}> × {item.quantity}</span>
                      </span>
                      <span className={s.itemPrice}>
                        ${parseFloat(item.price).toLocaleString("es-AR")} c/u
                      </span>
                    </li>
                  ))}
                </ul>
                <div className={s.financRow}>
                  <span>Financiamiento:</span>
                  <strong>
                    {paymentInformation.price_surcharge > 0
                      ? `+$${paymentInformation.price_surcharge}`
                      : paymentInformation.price_discount > 0
                      ? `-$${paymentInformation.price_discount} OFF`
                      : "$0"}
                  </strong>
                  {paymentInformation.extraQuantity > 0 && (
                    <span>│ Adicional por minoría: <strong>${paymentInformation.extraQuantity}</strong></span>
                  )}
                  {paymentInformation.extraDesign > 0 && (
                    <span>│ Cambio de diseño: <strong>${paymentInformation.extraDesign}</strong></span>
                  )}
                </div>
                {extraBreakdown.length > 0 && (
                  <div className={s.financRow}>
                    <span>Adicionales:</span>
                    {extraBreakdown.map((item, index) => (
                      <span key={`${item.name || "extra"}-${index}`}>
                        {item.name}: <strong>${calculateExtraTotal(item)}</strong>
                      </span>
                    ))}
                  </div>
                )}
              </details>
            )}
          </section>
        )}

        {/* ── ASESOR (mobile solamente — desktop lo muestra el sidebar) ── */}
        {(agentFirstName || agentSurname) && (
          <section className={`${s.section} ${s.sectionMobileOnly}`}>
            <h2 className={s.sectionTitle}>Tu asesor/a</h2>
            <div className={s.advisorCard}>
              <span className={s.advisorAvatar}><i className="bi bi-person-badge-fill" aria-hidden="true" /></span>
              <div className={s.advisorBody}>
                <span className={s.advisorLabel}>Mi asesor/a</span>
                <span className={s.advisorName}>
                  {agentFirstName} {agentSurname}
                </span>
                <span className={s.advisorHours}>Lun-Vie 9-13 / 14-18 hs</span>
              </div>
              <div className={s.advisorActions}>
                {agentPhone && (
                  <a
                    href={`https://wa.me/${agentPhone}`}
                    className={`${s.advisorBtn} ${s.advisorBtnWa}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp"
                  >
                    <i className="bi bi-whatsapp" aria-hidden="true" />
                  </a>
                )}
                <a
                  href="tel:39600101"
                  className={`${s.advisorBtn} ${s.advisorBtnPh}`}
                  aria-label="Llamar"
                >
                  <i className="bi bi-telephone-fill" aria-hidden="true" />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* ── ENCUESTA (mobile solamente) ── */}
        <section className={`${s.section} ${s.sectionMobileOnly}`}>
          <a
            href="https://forms.gle/8bTDRNQw1tT3VkSy5"
            className={s.surveyBtn}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-chat-left-heart-fill" aria-hidden="true" /> Nos interesa tu opinión
          </a>
        </section>

        {/* ── CHECKLIST DE TAREAS ── */}
        <section className={s.section}>
          <h2 className={s.sectionTitle}>
            {tasks.some((t) => t.status !== "done" && t.status !== "blocked")
              ? "Tus tareas pendientes"
              : "Estado de tu pedido"}
          </h2>
          <div className={s.taskList}>
            {tasks.map((task) => (
              <button
                key={task.id}
                className={`${s.taskItem} ${s[`task_${task.status}`]}`}
                onClick={task.onClick ?? undefined}
                disabled={!task.onClick}
                aria-label={task.label}
              >
                <span className={`${s.taskIcon} ${s[`taskIcon_${task.status}`]}`}>
                  <i className={task.iconClass} aria-hidden="true" />
                </span>
                <span className={s.taskBody}>
                  <span className={s.taskName}>{task.label}</span>
                  <span className={s.taskDesc}>{task.desc}</span>
                </span>
                <span className={`${s.taskCheck} ${s[`taskCheck_${task.status}`]}`}>
                  <i
                    className={
                      task.status === "done"
                        ? "bi bi-check-lg"
                        : task.status === "urgent"
                        ? "bi bi-exclamation-lg"
                        : task.status === "pending"
                        ? "bi bi-arrow-right"
                        : "bi bi-hourglass-split"
                    }
                    aria-hidden="true"
                  />
                </span>
              </button>
            ))}
          </div>
        </section>

        <div className={s.pageSpacer} />
      </div>
    </>
  );
}

export default Dashboard;
